import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { getMappingJob, cancelMappingJob, isMappingJobEndState } from '../../services/mappingJobService';

const DEFAULT_POLL_INTERVAL_MS = 3000;

const KNOWN_STATUSES = ['pending', 'running', 'completed', 'failed', 'cancelled', 'stopped_budget'];

const STATUS_COLOR = {
  pending: 'default',
  running: 'info',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
  stopped_budget: 'warning',
  unknown: 'default',
};

function formatCost(cost, locale, currency = 'EUR') {
  const value = Number.isFinite(cost) ? cost : 0;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
}

/**
 * JobMonitor (#97)
 * Beobachtet einen laufenden Mapping-Batch per Polling gegen
 * `GET /api/mapping/jobs/{id}` -- Status, Fortschritt, Kosten und Fehlerzahl,
 * mit Abbruchmoeglichkeit. Kein WebSocket/SSE, siehe Nicht-Ziele des Tickets.
 *
 * @param {object} props
 * @param {string|number} props.jobId - ID des zu beobachtenden Mapping-Jobs
 * @param {number} [props.pollIntervalMs] - Abstand zwischen zwei Abfragen
 * @param {function} [props.onStopped] - Callback, sobald ein Endzustand erreicht ist
 */
const JobMonitor = ({ jobId, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, onStopped }) => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';
  const unknownStatusLabel = t('jobMonitor.status.unknown');
  const loadErrorLabel = t('jobMonitor.loadError');

  const [job, setJob] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);
  // Sobald ein Endzustand angewendet wurde, wird jede weitere Antwort
  // verworfen -- sonst ueberschreibt eine noch laufende Poll-Antwort (die vor
  // dem Abbruch gestartet wurde) den Endzustand wieder mit "laeuft".
  const hasEndedRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const applyJobUpdate = useCallback((data) => {
    if (!isMountedRef.current || hasEndedRef.current) {
      return;
    }
    setJob(data);
    setLoadError(null);
    if (data && isMappingJobEndState(data.status)) {
      hasEndedRef.current = true;
      stopPolling();
      if (onStopped) {
        onStopped(data);
      }
    }
  }, [onStopped, stopPolling]);

  const fetchJob = useCallback(async () => {
    try {
      const data = await getMappingJob(jobId);
      applyJobUpdate(data);
    } catch (error) {
      if (isMountedRef.current) {
        setLoadError(error.message || loadErrorLabel);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, applyJobUpdate]);

  useEffect(() => {
    isMountedRef.current = true;
    hasEndedRef.current = false;
    fetchJob();
    intervalRef.current = window.setInterval(fetchJob, pollIntervalMs);

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, pollIntervalMs]);

  const handleCancel = async () => {
    // Kein manuelles stopPolling() hier: der Race zwischen einer haengenden
    // Poll-Antwort und der Abbruch-Antwort ist bereits ueber hasEndedRef
    // abgedeckt (siehe applyJobUpdate). Wuerde hier vorab gestoppt und der
    // Abbruch-Request schlaegt fehl, gaebe es keinen Ausloeser mehr, der das
    // Polling neu startet -- die Ansicht friert auf dem letzten Stand ein,
    // waehrend der Job im Hintergrund weiterlaeuft.
    setIsCancelling(true);
    try {
      const data = await cancelMappingJob(jobId);
      applyJobUpdate(data);
    } catch (error) {
      if (isMountedRef.current) {
        setLoadError(error.message || loadErrorLabel);
      }
    } finally {
      if (isMountedRef.current) {
        setIsCancelling(false);
      }
    }
  };

  if (!job) {
    return (
      <Paper sx={{ p: 2 }}>
        {loadError ? <Alert severity="warning">{loadError}</Alert> : <LinearProgress />}
      </Paper>
    );
  }

  const statusLabel = KNOWN_STATUSES.includes(job.status)
    ? t(`jobMonitor.status.${job.status}`)
    : unknownStatusLabel;
  const statusColor = STATUS_COLOR[job.status] || 'default';
  const canCancel = !isMappingJobEndState(job.status);
  const progressPercent = job.total > 0 ? Math.min(100, (job.done / job.total) * 100) : 0;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Chip label={statusLabel} color={statusColor} size="small" />
        {canCancel && (
          <Button
            size="small"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={isCancelling}
          >
            {isCancelling ? t('jobMonitor.cancelling') : t('jobMonitor.cancel')}
          </Button>
        )}
      </Box>

      <LinearProgress variant="determinate" value={progressPercent} sx={{ mb: 1 }} />
      <Typography variant="body2">{`${job.done} / ${job.total}`}</Typography>
      <Typography variant="body2">{`${t('jobMonitor.cost')}: ${formatCost(job.cost, locale, job.currency)}`}</Typography>

      {job.errorCount > 0 && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setShowErrors((value) => !value)}
            endIcon={showErrors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {`${t('jobMonitor.errors')} (${job.errorCount})`}
          </Button>
          <Collapse in={showErrors} unmountOnExit>
            <List dense>
              {job.errors.map((jobError) => (
                <ListItem key={jobError.id}>
                  <ListItemText primary={jobError.message} secondary={jobError.parameterId || undefined} />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}

      {loadError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {loadError}
        </Alert>
      )}
    </Paper>
  );
};

export default JobMonitor;
